import { useState } from 'react';
import axios from 'axios';
import { url } from '../env';
import { downloadPDF } from '../Views/Cotizador/functions/downloadPDF';
import { buildBudgetPdfFile } from '../Views/Cotizador/PDFComponent/buildBudgetPdfFile';

/**
 * Custom hook to handle budget PDF operations
 * @param {Object} persona - The person object with budget information
 * @param {Object} toast - Toast reference for showing notifications
 * @returns {Object} - { getBudgetPDFData, generateAndDownloadPDF, loading }
 */
export const useBudgetPDF = (persona, toast) => {
    const [loading, setLoading] = useState(false);

    /**
     * Fetches PDF data from backend and returns it as a File object.
     * If draftPreview is provided (from a recotización draft), generates the PDF
     * directly from the preview data without calling the server.
     * @param {Object} [draftPreview] - Optional preview data with { resumen, detalle, pdfPlazo }
     * @returns {Promise<File|null>} - PDF file object or null if error
     */
    const getBudgetPDFData = async (draftPreview = null) => {
        if (!persona?.infoBudget?.budget_id) {
            toast?.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'No hay información de cotización disponible',
                life: 3000
            });
            return null;
        }

        setLoading(true);
        try {
            let resumen, detalle, pdfPlazo;

            if (draftPreview && draftPreview.resumen && draftPreview.detalle) {
                // Use draft preview data directly (recotización flow)
                resumen = draftPreview.resumen;
                detalle = draftPreview.detalle;
                pdfPlazo = draftPreview.pdfPlazo || [];
            } else {
                // Fetch from server (normal flow)
                if (!persona?.infoBudget?.secret) {
                    toast?.current?.show({
                        severity: 'warn',
                        summary: 'Advertencia',
                        detail: 'No hay información de cotización disponible',
                        life: 3000
                    });
                    return null;
                }

                const { data } = await axios({
                    method: 'get',
                    url: `${url}/generateBudgetPDF`,
                    params: {
                        id: persona.infoBudget.budget_id,
                        secret: persona.infoBudget.secret
                    }
                });

                resumen = data.resumen;
                detalle = data.detalle;
                pdfPlazo = data.pdfPlazo || [];
            }

            if (resumen && detalle) {
                // 🔴 La generación vive en buildBudgetPdfFile: es la MISMA que usa
                // el envío de cotización por WhatsApp. Con dos implementaciones,
                // un cambio de diseño le llegaría a unos clientes y no a otros.
                const suffix = draftPreview ? '_ajustada' : '';
                const fileName = `cotizacion_${persona.infoBudget.budget_id}${suffix}.pdf`;

                return await buildBudgetPdfFile({ resumen, detalle, pdfPlazo }, fileName);
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudieron obtener los datos de la cotización',
                    life: 4000
                });
                return null;
            }
        } catch (error) {
            console.error('Error al obtener PDF:', error);
            toast?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al obtener el PDF. Por favor intenta de nuevo.',
                life: 5000
            });
            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Generates and downloads the PDF (original behavior)
     */
    const generateAndDownloadPDF = async () => {
        if (!persona?.infoBudget?.secret || !persona?.infoBudget?.budget_id) {
            toast?.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'No hay información de cotización disponible',
                life: 3000
            });
            return;
        }

        setLoading(true);
        try {
            const { data } = await axios({
                method: 'get',
                url: `${url}/generateBudgetPDF`,
                params: {
                    id: persona.infoBudget.budget_id,
                    secret: persona.infoBudget.secret
                }
            });

            if (data.resumen && data.detalle) {
                downloadPDF(data.resumen, data.detalle, data.pdfPlazo || []);
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudieron obtener los datos de la cotización',
                    life: 4000
                });
            }
        } catch (error) {
            console.error('Error al descargar PDF:', error);
            toast?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al descargar el PDF. Por favor intenta de nuevo.',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    return {
        getBudgetPDFData,
        generateAndDownloadPDF,
        loading
    };
};
